import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { IndianRupee, FileText, CheckCircle2, AlertTriangle, Send, ThumbsUp, ThumbsDown, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(d.toISOString().slice(0, 7));
  }
  return months;
};

const TranslatorWork = () => {
  const { user } = useOutletContext();
  const [workData, setWorkData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [projectActions, setProjectActions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showInvoice, setShowInvoice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');

  // Dispute dialog
  const [showDispute, setShowDispute] = useState(false);
  const [disputeProjectId, setDisputeProjectId] = useState('');
  const [disputeReason, setDisputeReason] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [workRes, invRes, actRes] = await Promise.all([
        api.get('/translator/my-work'),
        api.get('/translator/my-invoices'),
        api.get('/translator/project-actions').catch(() => ({ data: {} })),
      ]);
      setWorkData(workRes.data);
      setInvoices(invRes.data);
      setProjectActions(actRes.data || {});
    } catch (e) {
      toast.error('Failed to load work data');
    }
    setLoading(false);
  };

  const currentMonth = new Date().toISOString().slice(0, 7);
  const canSubmit = new Date().getDate() >= 25;
  const hasSubmitted = invoices.some(inv => inv.month === currentMonth);
  const monthOptions = getMonthOptions();

  const pricedProjects = useMemo(() => {
    if (!workData) return [];
    let list = workData.projects.filter(p => p.earnings_inr > 0);
    if (selectedMonth !== 'all') {
      const [filterYear, filterMonth] = selectedMonth.split('-');
      list = list.filter(p => {
        const d = p.due_date || '';
        if (!d) return false;
        try {
          if (d.includes('/')) {
            const parts = d.split(/[/,\s]+/);
            if (parts.length >= 3) {
              return String(parseInt(parts[2])) === filterYear && String(parseInt(parts[1])).padStart(2, '0') === filterMonth;
            }
          }
          const parsed = new Date(d);
          return parsed.getFullYear() === parseInt(filterYear) && (parsed.getMonth() + 1) === parseInt(filterMonth);
        } catch { return false; }
      });
    }
    return list;
  }, [workData, selectedMonth]);

  // Only count completed (file received) items for billing total
  const monthTotal = useMemo(() => pricedProjects.filter(p => p.billing_status === 'completed').reduce((s, p) => s + (p.earnings_inr || 0), 0), [pricedProjects]);
  const approvedCount = useMemo(() => pricedProjects.filter(p => projectActions[p.project_id] === 'approved').length, [pricedProjects, projectActions]);
  const disputedCount = useMemo(() => pricedProjects.filter(p => projectActions[p.project_id] === 'disputed').length, [pricedProjects, projectActions]);

  const handleApprove = async (projectId) => {
    try {
      await api.post('/translator/project-action', { project_id: projectId, action: 'approved' });
      setProjectActions(prev => ({ ...prev, [projectId]: 'approved' }));
      toast.success('Project approved');
    } catch (e) { toast.error('Failed'); }
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) { toast.error('Enter a reason'); return; }
    try {
      await api.post('/translator/project-action', { project_id: disputeProjectId, action: 'disputed', reason: disputeReason });
      setProjectActions(prev => ({ ...prev, [disputeProjectId]: 'disputed' }));
      toast.success('Dispute submitted — admin notified');
      setShowDispute(false); setDisputeReason(''); setDisputeProjectId('');
    } catch (e) { toast.error('Failed'); }
  };

  const handleApproveAll = async () => {
    const unapproved = pricedProjects.filter(p => !projectActions[p.project_id]);
    for (const p of unapproved) {
      try {
        await api.post('/translator/project-action', { project_id: p.project_id, action: 'approved' });
      } catch (e) {}
    }
    const updated = { ...projectActions };
    unapproved.forEach(p => { updated[p.project_id] = 'approved'; });
    setProjectActions(updated);
    toast.success(`Approved ${unapproved.length} projects`);
  };

  const handleSubmitInvoice = async () => {
    setSubmitting(true);
    try {
      const lineItems = pricedProjects.map(p => ({
        title: p.main_title, episode: p.episode_title, task: p.task,
        qty: p.qty, rate: p.rate_inr, rate_type: p.rate_type, amount: p.earnings_inr,
      }));
      await api.post('/translator/submit-invoice', {
        month: currentMonth, total_amount: monthTotal, line_items: lineItems,
      });
      toast.success('Invoice submitted!');
      setShowInvoice(false); loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  if (loading) return <DashboardLayout user={user}><div className="flex items-center justify-center h-96"><div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold text-primary" data-testid="my-work-title">My Work & Invoices</h1>
            <p className="text-muted-foreground">Your project earnings and invoice submission</p>
          </div>
          <div className="flex gap-2 items-center">
            {canSubmit && !hasSubmitted && pricedProjects.length > 0 && (
              <Button onClick={() => setShowInvoice(true)} data-testid="submit-invoice-btn">
                <Send className="w-4 h-4 mr-2" /> Submit Invoice
              </Button>
            )}
          </div>
        </div>

        {/* Summary + Month Filter */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {monthOptions.map(m => <SelectItem key={m} value={m}>{new Date(m + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {pricedProjects.some(p => !projectActions[p.project_id]) && (
            <Button variant="outline" size="sm" onClick={handleApproveAll}>
              <ThumbsUp className="w-4 h-4 mr-2" /> Approve All Pending
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-green-600">₹{monthTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-muted-foreground">Earnings</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{pricedProjects.length}</p>
              <p className="text-xs text-muted-foreground">Projects</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{approvedCount}</p>
              <p className="text-xs text-muted-foreground">Approved</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-red-600">{disputedCount}</p>
              <p className="text-xs text-muted-foreground">Disputed</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-400">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-gray-600">{pricedProjects.length - approvedCount - disputedCount}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
        </div>

        {!canSubmit && <Card className="border-l-4 border-l-amber-400"><CardContent className="pt-4 pb-3"><p className="text-sm"><AlertTriangle className="w-4 h-4 inline mr-1 text-amber-500" /> Invoice submission opens on the <strong>25th</strong>.</p></CardContent></Card>}
        {hasSubmitted && <Card className="border-l-4 border-l-green-400"><CardContent className="pt-4 pb-3"><p className="text-sm"><CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" /> Invoice for {currentMonth} submitted.</p></CardContent></Card>}

        {/* Projects Table */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><IndianRupee className="w-4 h-4 text-green-600" /> Your Projects</CardTitle></CardHeader>
          <CardContent className="p-0">
            {pricedProjects.length === 0 ? (
              <div className="py-12 text-center"><IndianRupee className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" /><p className="text-muted-foreground">No priced projects{selectedMonth !== 'all' ? ' for this month' : ''}.</p></div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-10 text-center">#</TableHead>
                    <TableHead className="text-center">Main Title</TableHead>
                    <TableHead className="text-center">Episode</TableHead>
                    <TableHead className="text-center">Task</TableHead>
                    <TableHead className="text-center">QTY</TableHead>
                    <TableHead className="text-center">Rate (₹)</TableHead>
                    <TableHead className="text-center">Earnings (₹)</TableHead>
                    <TableHead className="text-center">Due Date</TableHead>
                    <TableHead className="text-center">Approval</TableHead>
                    <TableHead className="text-center w-32">Action</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {pricedProjects.map((p, i) => {
                      const status = projectActions[p.project_id];
                      const billingDone = p.billing_status === 'completed';
                      return (
                        <TableRow key={p.project_id || i} className={!billingDone ? 'bg-amber-50/50' : status === 'disputed' ? 'bg-red-50' : status === 'approved' ? 'bg-green-50/50' : ''}>
                          <TableCell className="text-center text-muted-foreground text-xs">{i + 1}</TableCell>
                          <TableCell className="text-center text-sm">{p.main_title || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.episode_title || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.task || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.qty || '—'}</TableCell>
                          <TableCell className="text-center text-sm">₹{p.rate_inr}</TableCell>
                          <TableCell className="text-center font-semibold text-green-600">{billingDone ? `₹${p.earnings_inr?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : <span className="text-amber-600 font-normal">—</span>}</TableCell>
                          <TableCell className="text-center text-xs whitespace-nowrap">
                            {p.due_date
                              ? <span className="text-foreground">{p.due_date}</span>
                              : <Badge className="bg-amber-100 text-amber-700 text-[10px]">No Date</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {status === 'approved' ? <Badge className="bg-green-100 text-green-700 text-[10px]">Approved</Badge> :
                             status === 'disputed' ? <Badge className="bg-red-100 text-red-700 text-[10px]">Disputed</Badge> :
                             <Badge className="bg-gray-100 text-gray-500 text-[10px]">Pending</Badge>}
                          </TableCell>
                          <TableCell className="text-center">
                            {!status && (
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="sm" className="h-7 text-green-600" onClick={() => handleApprove(p.project_id)}>
                                  <ThumbsUp className="w-3.5 h-3.5 mr-1" /> OK
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => { setDisputeProjectId(p.project_id); setShowDispute(true); }}>
                                  <ThumbsDown className="w-3.5 h-3.5 mr-1" /> Dispute
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell colSpan={6} className="text-right">Total</TableCell>
                      <TableCell className="text-center text-green-700">₹{monthTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Past Invoices */}
        {invoices.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> My Invoices</CardTitle></CardHeader>
            <CardContent className="p-0"><div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead className="text-center">Invoice #</TableHead><TableHead className="text-center">Month</TableHead>
                <TableHead className="text-center">Amount (₹)</TableHead><TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Submitted</TableHead>
              </TableRow></TableHeader>
              <TableBody>{invoices.map(inv => (
                <TableRow key={inv.invoice_id}>
                  <TableCell className="text-center text-sm font-mono">{inv.invoice_number}</TableCell>
                  <TableCell className="text-center text-sm">{inv.month}</TableCell>
                  <TableCell className="text-center font-semibold text-green-600">₹{inv.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-center">
                    {inv.status === 'approved' ? <Badge className="bg-green-100 text-green-700 text-xs">Approved</Badge> :
                     inv.status === 'disputed' ? <Badge className="bg-red-100 text-red-700 text-xs">Disputed</Badge> :
                     <Badge className="bg-amber-100 text-amber-700 text-xs">Submitted</Badge>}
                  </TableCell>
                  <TableCell className="text-center text-xs text-muted-foreground">{new Date(inv.submitted_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table></div></CardContent>
          </Card>
        )}
      </div>

      {/* Submit Invoice Dialog */}
      <Dialog open={showInvoice} onOpenChange={setShowInvoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Submit Invoice — {currentMonth}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{pricedProjects.length} projects, {approvedCount} approved</p>
            <div className="border rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-600">₹{monthTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowInvoice(false)}>Cancel</Button><Button onClick={handleSubmitInvoice} disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Invoice'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={showDispute} onOpenChange={setShowDispute}>
        <DialogContent>
          <DialogHeader><DialogTitle><ThumbsDown className="w-5 h-5 inline mr-2 text-red-500" />Dispute Project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Reason for dispute</Label>
            <Input value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="Incorrect duration, wrong rate, etc." />
            <p className="text-xs text-muted-foreground">Admin/Owner will be notified of this dispute.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDispute(false)}>Cancel</Button><Button variant="destructive" onClick={handleDispute}>Submit Dispute</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default TranslatorWork;
